import { useState, useEffect } from 'react'
import AdminLayout from '@/layouts/AdminLayout'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import toast from 'react-hot-toast'
import { legalService } from '@services/api/legal'

const SLUG = 'cgu'

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'blockquote'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
}

function AdminTermsPage() {
  const [title, setTitle] = useState("Conditions Générales d'Utilisation")
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const res = await legalService.get(SLUG)
        if (res.success && res.data) {
          setTitle(res.data.title || "Conditions Générales d'Utilisation")
          setContent(res.data.content || '')
          setUpdatedAt(res.data.updated_at || null)
        }
      } catch (error) {
        console.error('Erreur chargement CGU:', error)
        toast.error('Erreur lors du chargement')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleSave = async () => {
    try {
      setSaving(true)
      const res = await legalService.update(SLUG, { title, content })
      if (res.success) {
        toast.success('Conditions générales enregistrées')
        setUpdatedAt(res.data?.updated_at || new Date().toISOString())
      }
    } catch (error) {
      console.error('Erreur enregistrement CGU:', error)
      toast.error("Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminLayout>
      <div className="w-full pt-6">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#002A4E]">Conditions générales</h1>
            <p className="text-sm text-gray-500 mt-1">
              Éditez le contenu affiché sur la page publique des conditions générales.
              {updatedAt && (
                <span className="ml-1">
                  Dernière mise à jour : {new Date(updatedAt).toLocaleString('fr-FR')}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-5 py-2.5 bg-[#FFD700] hover:bg-[#F5C800] text-[#002A4E] font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Enregistrer
              </>
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-md p-12 flex items-center justify-center border border-slate-200">
            <div className="w-8 h-8 border-2 border-[#F5B700] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-md p-6 space-y-5 border border-slate-200">
            {/* Titre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Titre</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre de la page"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300"
              />
            </div>

            {/* Contenu (éditeur Quill) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contenu</label>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
                className="bg-white [&_.ql-container]:min-h-[400px] [&_.ql-editor]:min-h-[400px]"
              />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminTermsPage
